import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackReportProductSubmit } from '../ga';
import './ReportModal.css';

const REASONS = [
  { value: 'wrong_price',       label: 'Wrong price' },
  { value: 'wrong_image',       label: 'Wrong or missing image' },
  { value: 'wrong_title',       label: 'Wrong title / name' },
  { value: 'wrong_description', label: 'Wrong or misleading description' },
  { value: 'unavailable',       label: 'Product is unavailable or discontinued' },
  { value: 'duplicate',         label: 'Duplicate listing' },
  { value: 'other',             label: 'Other' },
];

/**
 * @param {{
 *   onClose: () => void;
 *   productUrl: string;
 *   productTitle: string;
 * }} props
 */
export function ReportModal({ onClose, productUrl, productTitle }) {
  const titleId   = useId();
  const reasonId  = useId();
  const detailsId = useId();
  const firstRef  = useRef(null);

  const [reason,     setReason]     = useState('');
  const [details,    setDetails]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [notice,     setNotice]     = useState(null);

  useEffect(() => {
    setReason('');
    setDetails('');
    setError(null);
    setNotice(null);
    setSubmitting(false);
    const t = window.setTimeout(() => {
      firstRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/product-report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productUrl,
          productTitle: typeof productTitle === 'string' ? productTitle : '',
          reason,
          details: details.trim(),
        }),
      });
      const body = await jsonOrThrow(res);
      const n =
        body &&
        typeof body === 'object' &&
        typeof body.notice === 'string' &&
        body.notice.trim() !== ''
          ? body.notice.trim()
          : 'Thank you — your report has been submitted and will be reviewed.';
      setNotice(n);
      trackReportProductSubmit(reason);
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  const heading =
    typeof productTitle === 'string' && productTitle.trim() !== ''
      ? productTitle.trim()
      : 'this product';

  return createPortal(
    <div
      className="report-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="report-modal__header">
          <h2 id={titleId} className="report-modal__title">
            Report an issue
          </h2>
          <button
            type="button"
            className="report-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="report-modal__body">
          {notice == null ? (
            <>
              <p className="report-modal__lede">
                Something wrong with <strong>{heading}</strong>? Let us know and
                we will look into it.
              </p>
              {error !== null && (
                <p
                  className="report-modal__banner report-modal__banner--error"
                  role="alert"
                >
                  {error}
                </p>
              )}
              <form onSubmit={handleSubmit} noValidate>
                <div className="report-modal__field">
                  <label htmlFor={reasonId}>What is wrong?</label>
                  <select
                    ref={firstRef}
                    id={reasonId}
                    value={reason}
                    onChange={(ev) => setReason(ev.target.value)}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select a reason…</option>
                    {REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="report-modal__field">
                  <label htmlFor={detailsId}>
                    Additional details{' '}
                    <span style={{ fontWeight: 400, color: '#64748b' }}>
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id={detailsId}
                    value={details}
                    onChange={(ev) => setDetails(ev.target.value)}
                    placeholder="Describe the issue in more detail…"
                    maxLength={4000}
                    disabled={submitting}
                    rows={4}
                  />
                </div>
                <div className="report-modal__actions">
                  <button
                    type="submit"
                    className="report-modal__submit"
                    disabled={submitting || reason === ''}
                  >
                    {submitting ? 'Submitting…' : 'Submit report'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p
                className="report-modal__banner report-modal__banner--success"
                role="status"
              >
                {notice}
              </p>
              <div className="report-modal__actions">
                <button
                  type="button"
                  className="report-modal__submit"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
